import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { PieChart, Pie, Tooltip, Cell, ResponsiveContainer } from "recharts";

const COLORS = {
  New: "#2053c3",
  Completed: "#0f9f6e",
  Pending: "#dc4d5b",
  "Completed After Deadline": "#f0a33b",
};

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 1 + i);

const ActionablesAtGlance = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const policyQuery = query.get("policy") || "";

  const [filters, setFilters] = useState({
    fromMonth: "",
    fromYear: CURRENT_YEAR,
    toMonth: "",
    toYear: CURRENT_YEAR,
    location: "",
    type: "",
    frequency: "",
  });

  const [locations, setLocations] = useState([]);
  const [types, setTypes] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [statusTriggerData, setStatusTriggerData] = useState([]);
  const [actionables, setActionables] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [showScroll, setShowScroll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loggedInUsername, setLoggedInUsername] = useState(
    localStorage.getItem("username") || ""
  );

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setLoggedInUsername("");
      return;
    }

    axios
      .get("/api/users/me/", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const username =
          res.data.username || res.data.user_id || res.data.id || "";

        if (username) {
          setLoggedInUsername(username);
          localStorage.setItem("username", username);
        } else {
          setLoggedInUsername("");
          localStorage.removeItem("username");
        }
      })
      .catch(() => {
        setLoggedInUsername(localStorage.getItem("username") || "");
      });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");

    axios
      .get("/api/actionables/filters/", {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      .then((res) => {
        setLocations(res.data.locations || []);
        setTypes(res.data.types || []);
        setFrequencies(res.data.frequencies || []);
      })
      .catch((err) => console.error("Error fetching filter options:", err));
  }, []);

  const fetchData = async () => {
    if (!loggedInUsername) {
      setPieData([]);
      setStatusTriggerData([]);
      setActionables([]);
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("accessToken");
      const payload = { ...filters, username: loggedInUsername };

      if (policyQuery) {
        payload.policy_or_document_name = policyQuery;
      }

      const res = await axios.post("/api/actionables/summary/", payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      setPieData(res.data.pieData || []);
      setStatusTriggerData(res.data.statusTriggerData || []);
      setActionables(res.data.actionables || []);
    } catch (err) {
      console.error("Error fetching actionables summary:", err);
      setPieData([]);
      setStatusTriggerData([]);
      setActionables([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [loggedInUsername, policyQuery]);

  useEffect(() => {
    const handleScroll = () => setShowScroll(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const totals = (statusTriggerData || []).reduce(
    (acc, row) => {
      acc.totalSystem += row["ISO System Trigger"] || 0;
      acc.totalExternal += row["ISO External Trigger"] || 0;
      return acc;
    },
    { totalSystem: 0, totalExternal: 0 }
  );

  const completePieData = Object.keys(COLORS).map((status) => {
    const found = (pieData || []).find((item) => item.status === status);
    return { status, count: found ? found.count : 0 };
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handlePieClick = (data) => {
    if (!data?.status) return;

    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    if (policyQuery) {
      params.append("policy", policyQuery);
    }

    navigate(`/actionables-by-status/${data.status}?${params.toString()}`);
  };

  return (
    <div className="container">
      <div className="compact-hero">
        <div className="header-row" style={{ marginBottom: 0 }}>
          <div>
            <h1 className="header">Actionables At a Glance</h1>
            <p
              style={{
                margin: "10px 0 0",
                color: "rgba(255,255,255,0.82)",
                lineHeight: 1.7,
              }}
            >
              Review status trends, trigger distribution, and detailed actionable
              records from one cleaner analytics view.
            </p>
          </div>
          <button className="back-button" onClick={() => navigate("/dashboard")}>
            Back to Dashboard
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <strong>User:</strong> <span className="tag">{loggedInUsername || "Not signed in"}</span>
        {policyQuery && (
          <>
            <span style={{ marginLeft: 12 }}>
              <strong>Policy filter:</strong>
            </span>{" "}
            <span className="tag">{policyQuery}</span>
            <button
              className="clear-button"
              onClick={() => {
                const url = new URL(window.location.href);
                url.searchParams.delete("policy");
                window.location.href = url.toString();
              }}
            >
              Clear
            </button>
          </>
        )}
      </div>

      <div className="filters">
        {[
          { label: "From Month", name: "fromMonth", options: MONTHS },
          { label: "From Year", name: "fromYear", options: YEARS },
          { label: "To Month", name: "toMonth", options: MONTHS },
          { label: "To Year", name: "toYear", options: YEARS },
          { label: "Location", name: "location", options: locations, all: true },
          { label: "Type", name: "type", options: types, all: true },
          {
            label: "Frequency",
            name: "frequency",
            options: frequencies,
            all: true,
          },
        ].map((field) => (
          <div key={field.name} className="filter-item">
            <label>{field.label}</label>
            <select
              name={field.name}
              value={filters[field.name]}
              onChange={handleFilterChange}
            >
              {field.all && <option value="">All</option>}
              {(field.options || []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        ))}

        <div className="filter-item">
          <button onClick={fetchData} className="apply-button">
            Apply Filters
          </button>
        </div>
      </div>

      <div
        className="metric-grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", marginBottom: "1.4rem" }}
      >
        {[
          { label: "Visible Actionables", value: actionables.length },
          { label: "System Trigger Total", value: totals.totalSystem },
          { label: "External Trigger Total", value: totals.totalExternal },
        ].map((item) => (
          <div key={item.label} className="surface-card" style={{ padding: "1.2rem" }}>
            <div style={{ fontSize: "0.82rem", color: "#65748f", fontWeight: 700 }}>
              {item.label}
            </div>
            <div style={{ marginTop: "8px", fontSize: "1.45rem", fontWeight: 800, color: "#102a71" }}>
              {item.value}
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0, color: "#102a71" }}>Actionables by Status</h2>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={completePieData}
                dataKey="count"
                nameKey="status"
                outerRadius={110}
                label
                onClick={({ payload }) => handlePieClick(payload)}
                isAnimationActive
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {completePieData.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={COLORS[entry.status]}
                    style={{
                      transform: activeIndex === index ? "scale(1.05)" : "scale(1)",
                      transformOrigin: "center",
                      transition: "transform 0.2s ease-in-out",
                      cursor: "pointer",
                    }}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="status-legend">
          {completePieData.map((entry) => (
            <div key={entry.status} className="status-item">
              <span
                className="status-color"
                style={{ backgroundColor: COLORS[entry.status] }}
              />
              <span className="status-label">
                {entry.status}: {entry.count}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0, color: "#102a71" }}>Status vs Trigger Type</h2>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>ISO System Trigger</th>
                <th>ISO External Trigger</th>
              </tr>
            </thead>
            <tbody>
              {statusTriggerData.map((row) => (
                <tr key={row.status}>
                  <td>{row.status}</td>
                  <td>{row["ISO System Trigger"]}</td>
                  <td>{row["ISO External Trigger"]}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td>Total</td>
                <td>{totals.totalSystem}</td>
                <td>{totals.totalExternal}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0, color: "#102a71" }}>Detailed Actionables</h2>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  {[
                    "Actionable_Number",
                    "Actionable_Name",
                    "Location",
                    "Type",
                    "Category",
                    "Frequency",
                    "Start_Date",
                    "End_date",
                    "Status",
                    "Actionable_Description",
                    "Username",
                    "Annexure_Control_Clause",
                    "Policy_Or_Document_Name",
                  ].map((column) => (
                    <th key={column}>{column.replace(/_/g, " ")}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {actionables.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="no-data">
                      No data found.
                    </td>
                  </tr>
                ) : (
                  actionables.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <a href={`/actionable/${item.uat_id}`} className="link">
                          {item.actionable_number}
                        </a>
                      </td>
                      <td>{item.actionable_name}</td>
                      <td>{item.location}</td>
                      <td>{item.type}</td>
                      <td>{item.category}</td>
                      <td>{item.frequency}</td>
                      <td>{item.start_date}</td>
                      <td>{item.end_date}</td>
                      <td>{item.status}</td>
                      <td>{item.actionable_description}</td>
                      <td>{item.username}</td>
                      <td>{item.annexure_control_clause}</td>
                      <td>{item.policy_or_document_name}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showScroll && (
        <button className="scroll-top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          ^
        </button>
      )}

      <style>{`
        .container {
          padding-top: 24px;
        }

        .header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 18px;
        }

        .tag {
          margin-bottom: 10px;
        }

        .filter-item {
          display: flex;
          flex-direction: column;
        }

        .filter-item label {
          margin-bottom: 6px;
        }

        .chart-container {
          width: 100%;
          height: 300px;
        }

        .status-legend {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          margin-top: 15px;
          gap: 15px;
        }

        .status-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: #3a4b6d;
        }

        .status-color {
          width: 16px;
          height: 16px;
          border-radius: 50%;
        }

        .total-row td {
          background: rgba(16, 42, 113, 0.08) !important;
          color: #102a71;
          font-weight: 800;
        }

        .link {
          text-decoration: none;
        }

        .scroll-top {
          font-size: 18px;
        }
      `}</style>
    </div>
  );
};

export default ActionablesAtGlance;
